import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Search, Link2, CreditCard, Receipt, ShieldCheck, Info, Award } from "lucide-react";
import { toast } from "sonner";
import { Spinner, StatusBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { FloorAssessment } from "../../components/FloorAssessment";
import { staffLabelText } from "../../components/StaffAvatar";
import { CertificateChip, CertificateVerifyLink, PublicCertificateDownloadButton, originParam } from "../../components/TransferCertificate";  // Phase 124
import { api, errMsg, fmtINR, fmtDate } from "../../lib/api";
import { pokeLiveCollections } from "../../lib/liveCollections";
import { useLiveCollections } from "../../lib/liveCollections";
import { useRoleModules } from "../../lib/roleModules";
import { useReceiptVisibility } from "../../lib/receiptVisibility";   // Phase 169
import { PaymentRecordedDialog, ReceiptHiddenBadge, ReceiptHiddenBanner } from "../../components/ReceiptVisibilityNotice";   // Phase 169
import { PropertyContactBlock, UnlinkPropertyButton } from "../../components/CitizenPropertyCard";   // Phase 205/206
import { TaxHeadPicker, initialSelection, summarizeSelection } from "../../components/TaxHeadPicker";   // Phase 229

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

function PayOnlineDialog({ code, prop, demands, open, onClose, configured, onPaid, onRecorded }) {
  const balance = Math.round(demands.reduce((s, d) => s + d.balance_amount, 0) * 100) / 100;
  const [amount, setAmount] = useState(balance);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  // Phase 229: several tax heads on the property -> the citizen ticks the head(s) / year(s) and an amount for each
  // (everything pre-ticked in full); only those demands are settled. One head -> the plain amount box as before.
  const [headSel, setHeadSel] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      api.get(`/t/${code}/payments/quote`, { params: { property_id: prop.id } })
        .then((r) => { setQuote(r.data); setAmount(r.data.totals.net_payable_full); setHeadSel(r.data.multi_head ? initialSelection(r.data.demands) : {}); })
        .catch(() => { setQuote(null); setAmount(balance); setHeadSel({}); });
    }
  }, [open, balance, code, prop.id]);

  const maxPayable = quote ? quote.totals.net_payable_full : balance;
  const headsMode = !!quote?.multi_head;
  const headSummary = useMemo(() => summarizeSelection(quote?.demands || [], headSel), [quote, headSel]);
  const payAmount = headsMode ? headSummary.total : Number(amount) || 0;
  const headsOk = !headsMode || (headSummary.errors.length === 0 && headSummary.selectedCount > 0);

  const pay = async () => {
    const amt = payAmount;
    if (headsMode && headSummary.selectedCount === 0) return toast.error("Tick at least one tax head to pay");
    if (headsMode && headSummary.errors.length) return toast.error(headSummary.errors[0]);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > maxPayable + 0.004) return toast.error(`Amount cannot exceed net payable of ${fmtINR(maxPayable)}`);
    setBusy(true);
    try {
      const payload = headsMode ? { property_id: prop.id, amount: amt, allocations: headSummary.allocations } : { property_id: prop.id, amount: amt };
      const { data: order } = await api.post(`/t/${code}/citizen/pay-online/order`, payload);
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Could not load Razorpay checkout. Check your internet connection.");
        setBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: order.name,
        description: order.description,
        order_id: order.order_id,
        prefill: order.prefill || {},
        theme: { color: "#0b5686" },
        handler: async (resp) => {
          try {
            const { data: v } = await api.post(`/t/${code}/citizen/pay-online/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Payment successful • Receipt ${v.receipt_number}`);
            pokeLiveCollections();   // Phase 149
            onPaid();
            onClose();
            if (v.receipt_visible === false) {   // Phase 169: receipt hidden for citizens — confirm here, do not open the receipt
              onRecorded?.({ receiptNumber: v.receipt_number, amount: fmtINR(v.amount ?? v.receipt?.total_amount), message: v.receipt_hidden_message });
              return;
            }
            if (v.receipt_number) navigate(`/${code}/receipt/${v.receipt_number}`);
          } catch (err) {
            toast.error(errMsg(err));
          }
        },
        modal: { ondismiss: () => { setBusy(false); toast.info("Payment cancelled"); } },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed. Please try again.");
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={headsMode ? "max-w-lg" : "max-w-md"}>
        <DialogHeader>
          <DialogTitle className="font-heading">Pay online</DialogTitle>
          <DialogDescription>
            Property <span className="font-mono">{prop.pin}</span> • Pending dues {fmtINR(balance)}
          </DialogDescription>
        </DialogHeader>
        {quote && (
          <div className="rounded-lg border bg-accent/40 px-3 py-2 text-sm space-y-1" data-testid="citizen-quote-summary">
            <div className="flex justify-between"><span className="text-muted-foreground">Total due</span><span className="tabular-nums">{fmtINR(quote.totals.due_with_penalty)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Penalty</span><span className="tabular-nums text-amber-700" data-testid="citizen-quote-penalty">{fmtINR(quote.totals.penalty)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Discount (on full payment)</span><span className="tabular-nums text-emerald-700" data-testid="citizen-quote-discount">{fmtINR(quote.totals.discount)}</span></div>
            <div className="flex justify-between font-semibold border-t pt-1"><span>Net payable</span><span className="tabular-nums" data-testid="citizen-quote-net">{fmtINR(quote.totals.net_payable_full)}</span></div>
            {quote.totals.discount > 0 && (
              <p className="text-[11px] text-muted-foreground">Discount applies only when dues are settled in full — partial payments forfeit it.</p>
            )}
          </div>
        )}
        {!configured ? (
          <Alert data-testid="pay-online-not-configured">
            <Info className="h-4 w-4" />
            <AlertTitle>Online payments not enabled yet</AlertTitle>
            <AlertDescription>
              The municipality has not configured its Razorpay payment gateway yet. Meanwhile, you can pay
              at the municipal counter or to a field tax collector.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-4">
            {headsMode ? (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="pay-online-heads-step">
                <Label>Which tax heads are you paying?</Label>
                <TaxHeadPicker lines={quote.demands} value={headSel} onChange={setHeadSel} disabled={busy} testIdPrefix="citizen-heads" />
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Amount to pay (INR)</Label>
                <Input type="number" min="1" max={maxPayable} value={amount}
                  onChange={(e) => setAmount(e.target.value)} data-testid="pay-online-amount-input" />
                <p className="text-xs text-muted-foreground">
                  Partial payments allowed. Amount is applied to oldest dues first.
                </p>
              </div>
            )}
            <Button className="w-full" onClick={pay} disabled={busy || !headsOk} data-testid="pay-online-proceed-button">
              <ShieldCheck className="h-4 w-4 mr-2" /> {busy ? "Opening secure checkout..." : `Pay ${fmtINR(payAmount)} securely`}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Secured by Razorpay • UPI, cards, net banking supported
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PropertyCard({ code, prop, demands, onLink, linked, onRefresh, payConfigured, canLink = true, canPay = true, onRecorded }) {
  const [payOpen, setPayOpen] = useState(false);

  return (
    <Card className="shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <p className="font-mono font-semibold">{prop.pin}</p>
            <p className="font-medium">{prop.owner_name}</p>
            <p className="text-sm text-muted-foreground">{prop.address}</p>
            <p className="text-xs text-muted-foreground mt-1">{prop.zone_label} • {prop.ward_label} • {prop.mohalla_label || ""}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={prop.payment_status} />
            {!linked && onLink && canLink && (
              <Button size="sm" variant="outline" onClick={onLink} data-testid="link-property-button">
                <Link2 className="h-3.5 w-3.5 mr-1" /> Link to my account
              </Button>
            )}
            {linked && canLink && (
              <UnlinkPropertyButton code={code} prop={prop} onUnlinked={() => onRefresh?.()} testId={`my-property-unlink-${prop.pin}`} />
            )}
          </div>
        </div>

        {linked && (prop.contact || prop.contact_message) && (
          <div className="mt-3">
            <PropertyContactBlock contact={prop.contact} message={prop.contact_message} testId={`my-property-contact-${prop.pin}`} />
          </div>
        )}

        {demands.length > 0 && (
          <div className="mt-4 border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tax Head</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {demands.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="text-sm">{d.tax_head_name}<p className="text-[11px] text-muted-foreground">{d.fy_label}</p></TableCell>
                    <TableCell className="text-right tabular-nums">{fmtINR(d.total_demand)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtINR(d.paid_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtINR(d.balance_amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {Array.isArray(prop.assessment_floors) && prop.assessment_floors.length > 0 && (
          <div className="mt-3">
            <FloorAssessment floors={prop.assessment_floors} />
          </div>
        )}

        {canPay ? (
        <div className="flex gap-2 mt-3 flex-wrap items-center">
          <Button size="sm" onClick={() => setPayOpen(true)} disabled={demands.every((d) => d.balance_amount <= 0)} data-testid="citizen-pay-now-button">
            <CreditCard className="h-3.5 w-3.5 mr-1" /> Pay Online
          </Button>
          {!payConfigured && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border" data-testid="pay-online-unavailable-badge">
              Online payment not yet enabled
            </span>
          )}
          <p className="text-xs text-muted-foreground self-center">or pay via field collector / municipal counter</p>
        </div>
        ) : (
          <p className="text-xs text-muted-foreground mt-3" data-testid="pay-online-disabled-note">Pay via field collector / municipal counter</p>
        )}
        {canPay && <PayOnlineDialog code={code} prop={prop} demands={demands} open={payOpen} configured={payConfigured}
          onClose={() => setPayOpen(false)} onPaid={onRefresh} onRecorded={onRecorded} />}
      </CardContent>
    </Card>
  );
}

export default function MyProperties() {
  const { code } = useParams();
  // Phase 152: citizen sub-features — my_properties (link), pay_online, transfer_certificates
  const access = useRoleModules(code);
  const canLink = access.canWrite("properties", "my_properties");
  const canPay = access.featureAllowed("properties", "pay_online");
  const showCerts = access.featureAllowed("properties", "transfer_certificates");
  const [myProps, setMyProps] = useState(null);
  const [pin, setPin] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [payments, setPayments] = useState([]);
  const [detailMap, setDetailMap] = useState({});
  const [payConfigured, setPayConfigured] = useState(false);
  const [certificates, setCertificates] = useState([]); // Phase 124: ownership transfer certificates for linked properties
  const rv = useReceiptVisibility(code);   // Phase 169: may this citizen open receipts right now?
  const [recorded, setRecorded] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get(`/t/${code}/citizen/my-properties`);
    setMyProps(r.data);
    const details = await Promise.all(r.data.map((p) => api.get(`/t/${code}/citizen/search`, { params: { pin: p.pin } }).then((x) => [p.id, x.data.demands]).catch(() => [p.id, []])));
    setDetailMap(Object.fromEntries(details));
    api.get(`/t/${code}/payments`).then((x) => setPayments(x.data)).catch(() => {});
    if (canPay) api.get(`/t/${code}/citizen/pay-online/status`).then((x) => setPayConfigured(!!x.data.configured)).catch(() => {});
    if (showCerts) api.get(`/t/${code}/citizen/transfer-certificates?${originParam()}`).then((x) => setCertificates(x.data.items || [])).catch(() => setCertificates([]));
    else setCertificates([]);
  }, [code, canPay, showCerts]);

  useEffect(() => { load().catch(() => {}); }, [load]);
  useLiveCollections(code, () => { load().catch(() => {}); });   // Phase 149: dues / receipts follow the live ledger

  const doSearch = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    setSearching(true);
    setSearchResult(null);
    try {
      const r = await api.get(`/t/${code}/citizen/search`, { params: { pin: pin.trim() } });
      setSearchResult(r.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSearching(false);
    }
  };

  const [linkError, setLinkError] = useState(null);   // Phase 205: conflict message from verify / link
  const linkProp = async () => {
    setLinkError(null);
    try {
      const v = await api.get(`/t/${code}/citizen/verify-pin`, { params: { pin: searchResult.property.pin } });
      if (!v.data.can_link) { setLinkError(v.data.message); return; }
      const r = await api.post(`/t/${code}/citizen/link-property`, { pin: searchResult.property.pin });
      toast.success(r.data.already ? "This property is already linked to your account" : "Property linked to your account");
      setSearchResult(null);
      setPin("");
      load();
    } catch (err) {
      if (err?.response?.status === 409) setLinkError(errMsg(err));
      else toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold font-heading">My Properties</h1>
          <p className="text-muted-foreground text-sm">Search by PIN, link properties, view demand and pay</p>
        </div>
        {canLink && (
          <Button asChild variant="outline"><Link to={`/${code}/portal/link-properties`} data-testid="my-properties-link-page-button"><Link2 className="h-4 w-4 mr-2" /> Link Your Properties</Link></Button>
        )}
      </div>

      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Search property by PIN</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={doSearch} className="flex gap-2">
            <Input placeholder="e.g. BMC100001" value={pin} onChange={(e) => { setPin(e.target.value); setLinkError(null); }} data-testid="citizen-property-search-input" />
            <Button type="submit" disabled={searching} data-testid="citizen-property-search-button">
              <Search className="h-4 w-4 mr-2" /> {searching ? "Searching..." : "Search"}
            </Button>
          </form>
          {linkError && (
            <Alert variant="destructive" className="mt-3" data-testid="citizen-link-conflict" role="alert">
              <Info className="h-4 w-4" />
              <AlertTitle>This property cannot be linked</AlertTitle>
              <AlertDescription data-testid="citizen-link-conflict-message">{linkError}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {searchResult && (
        <PropertyCard code={code} prop={searchResult.property} demands={searchResult.demands}
          linked={false} onLink={linkProp} onRefresh={load} payConfigured={payConfigured} canLink={canLink} canPay={canPay} onRecorded={setRecorded} />
      )}

      {!myProps ? <Spinner /> : myProps.length === 0 && !searchResult ? (
        <EmptyState title="No properties linked" subtitle="Search your property by PIN above and link it to your account" />
      ) : (
        <div className="space-y-4">
          {myProps.map((p) => (
            <PropertyCard key={p.id} code={code} prop={p} demands={detailMap[p.id] || []} linked onRefresh={load} payConfigured={payConfigured} canLink={canLink} canPay={canPay} onRecorded={setRecorded} />
          ))}
        </div>
      )}

      {showCerts && certificates.length > 0 && (
        <Card className="shadow-sm" data-testid="citizen-certificates-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><Award className="h-4 w-4" /> Ownership transfer certificates</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {certificates.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 flex-wrap border rounded-lg px-3 py-2" data-testid={`citizen-certificate-${c.certificate_number}`}>
                <div className="min-w-0">
                  <p className="text-sm"><span className="font-mono text-xs">{c.pin}</span> — {c.old_owner_name} <span className="text-muted-foreground">→</span> <span className="font-medium">{c.new_owner_name}</span></p>
                  <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                    <CertificateChip certificateNumber={c.certificate_number} testId={`citizen-certificate-chip-${c.certificate_number}`} />
                    <span>approved {c.approved_date || fmtDate(c.certificate_issued_at)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <CertificateVerifyLink code={code} token={c.public_token} testId={`citizen-certificate-verify-${c.certificate_number}`} />
                  <PublicCertificateDownloadButton code={code} token={c.public_token} certificateNumber={c.certificate_number} variant="outline"
                    testId={`citizen-certificate-download-${c.certificate_number}`} />
                </div>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground">Issued when the municipality approved the change of owner on your property. Anyone can verify a certificate by scanning the QR code printed on it.</p>
          </CardContent>
        </Card>
      )}

      <PaymentRecordedDialog open={!!recorded} onClose={() => setRecorded(null)} {...(recorded || {})} testId="citizen-payment-recorded" />
      {payments.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><Receipt className="h-4 w-4" /> My receipts</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <ReceiptHiddenBanner rv={rv} testId="citizen-receipts-hidden-banner" />
            {payments.map((p) => {
              // Phase 169: the server says whether THIS receipt may be opened (toggle + calendar window + payment-date gate)
              const canOpen = p.receipt_visible !== false && rv.canView(p.payment_date);
              const body = (
                <>
                  <div>
                    <p className="font-mono text-sm">{p.receipt_number}</p>
                    <p className="text-xs text-muted-foreground">{p.payment_mode} • {fmtDate(p.payment_date)}</p>
                    {p.collected_by_name && (
                      <p className="text-xs text-muted-foreground" data-testid={`citizen-receipt-collector-${p.receipt_number}`}>
                        Collected by {staffLabelText(p.collected_by_name, p.collected_by_employee_id)}
                      </p>
                    )}
                    {!canOpen && <div className="mt-1"><ReceiptHiddenBadge testId={`citizen-receipt-hidden-${p.receipt_number}`} /></div>}
                  </div>
                  <p className="font-semibold tabular-nums">{fmtINR(p.amount)}</p>
                </>
              );
              return canOpen ? (
                <Link key={p.id} to={`/${code}/receipt/${p.receipt_number}`} className="flex items-center justify-between border rounded-lg px-3 py-2 hover:bg-muted/50" data-testid={`citizen-receipt-${p.receipt_number}`}>{body}</Link>
              ) : (
                <div key={p.id} className="flex items-center justify-between border border-dashed rounded-lg px-3 py-2 bg-muted/30" data-testid={`citizen-receipt-${p.receipt_number}`} aria-disabled="true">{body}</div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
